import { propertiesTypes } from '@/reports/types';
import { ColumnValue } from '@/theme/index';
import { commaFormat, convertNumberToString } from '@/utils/index';

const getInteriorDimensionsDetails = (
  firstBuilding: propertiesTypes.Building | undefined
) => {
  const { basement, garage, attics, additions } =
    firstBuilding?.square_footage || {};
  const { total: totalBasement } = basement || {};
  const buildingArea = firstBuilding?.area || 0;

  return {
    dimensionsBuildingArea: buildingArea
      ? `${commaFormat(buildingArea)} Sq Ft`
      : '-',
    additions: additions ? `${commaFormat(additions)} Sq Ft` : '-',
    attics: attics ? `${commaFormat(attics)} Sq Ft` : '-',
    basement: totalBasement ? `${commaFormat(totalBasement)} Sq Ft` : '-',
    garage: garage ? `${commaFormat(garage)} Sq Ft` : '-',
  };
};

export const getOverallAttributesDetails = (
  firstBuilding: propertiesTypes.Building | undefined
) => {
  const getBuildingStyle = () => {
    const buildingStyle = firstBuilding?.style;
    const isUnknown = buildingStyle?.indexOf('Unknown') !== -1;
    return buildingStyle && !isUnknown ? buildingStyle : '-';
  };

  const getConstructionType = () => {
    const constructionType = firstBuilding?.construction_type;
    return constructionType && constructionType !== 'Unknown or not provided'
      ? constructionType
      : '-';
  };

  const getYearBuilt = () => {
    let value = '-';
    if (firstBuilding?.year_built) {
      value = `${firstBuilding.year_built}`;

      if (firstBuilding.year_built_effective) {
        value += `, ${firstBuilding.year_built_effective} Effective Year Built`;
      }
    }
    return value;
  };

  const buildingStories = firstBuilding?.number_of_stories
    ? convertNumberToString(firstBuilding?.number_of_stories)
    : '-';
  const buildingUnits = firstBuilding?.number_of_units
    ? convertNumberToString(firstBuilding?.number_of_units)
    : '-';
  const buildingArea = firstBuilding?.area || 0;

  return {
    constructionType: getConstructionType(),
    buildingClass: firstBuilding?.class || '-',
    buildingStories: buildingStories || '-',
    buildingStyle: getBuildingStyle(),
    buildingUnits,
    overallBuildingArea: buildingArea
      ? `${commaFormat(buildingArea)} Sq Ft`
      : '-',
    yearBuilt: getYearBuilt(),
  };
};

const getLotDetails = (rawProperty: propertiesTypes.Property) => {
  const { size, width, depth } = rawProperty.dimensions || {};
  const { zoning, land_use } = rawProperty.legal || {};
  const { standardized_land_use_code, land_use_code } = land_use || {};

  return {
    buildings: rawProperty.buildings?.length
      ? convertNumberToString(rawProperty.buildings?.length)
      : '-',
    landUse: land_use_code || '-',
    lotDepth: depth ? `${commaFormat(depth)} Sq Ft` : '-',
    lotFrontage: width ? `${commaFormat(width)} Sq Ft` : '-',
    lotSize: size ? `${commaFormat(size)} Sq Ft` : '-',
    standardizedLandUse: standardized_land_use_code || '-',
    zoning: zoning || '-',
  };
};

export const getRoomsCountDetails = (
  firstBuilding: propertiesTypes.Building | undefined
) => {
  const {
    total: roomsCount,
    bed: bedrooms,
    baths: { total: bathrooms = 0 } = {},
  } = firstBuilding?.rooms || {};

  const bathBasement = firstBuilding?.rooms?.baths?.basement;
  const buildingBathBasement =
    bathBasement?.['1/2'] || bathBasement?.full ? 'Yes' : 'No';
  return {
    roomsCount: roomsCount ? convertNumberToString(roomsCount) : '-',
    bedrooms: bedrooms ? convertNumberToString(bedrooms) : '-',
    bathrooms: bathrooms ? convertNumberToString(bathrooms) : '-',
    buildingBathBasement: buildingBathBasement || '-',
  };
};

const checkIfHasData = (object: object) =>
  Object.values(object).some((el) => el !== '-');

export function formatLotAndBuildings(rawProperty: propertiesTypes.Property) {
  const firstBuilding = rawProperty.buildings?.[0];

  // Interior Dimensions
  const interiorDimensionsDetails = getInteriorDimensionsDetails(firstBuilding);
  const hasInteriorDimensionsData = checkIfHasData(interiorDimensionsDetails);
  const { basement, dimensionsBuildingArea, garage, attics, additions } =
    interiorDimensionsDetails;

  // Overall Attributes
  const overallAttributesDetails = getOverallAttributesDetails(firstBuilding);
  const hasOverallAttributesData = checkIfHasData(overallAttributesDetails);
  const {
    constructionType,
    buildingStories,
    buildingUnits,
    buildingClass,
    buildingStyle,
    yearBuilt,
    overallBuildingArea,
  } = overallAttributesDetails;

  // Lot Details
  const lotDetails = getLotDetails(rawProperty);
  const hasLotDetailsData = checkIfHasData(lotDetails);
  const {
    buildings,
    landUse,
    lotDepth,
    lotFrontage,
    lotSize,
    standardizedLandUse,
    zoning,
  } = lotDetails;

  // Room Counts
  const roomCountsDetails = getRoomsCountDetails(firstBuilding);
  const hasRoomCountsData = checkIfHasData(roomCountsDetails);
  const { roomsCount, bedrooms, bathrooms, buildingBathBasement } =
    roomCountsDetails;

  return {
    lotDetails: {
      hasData: hasLotDetailsData,
      rows: [
        ['Land use', standardizedLandUse],
        ['County land use code', landUse],
        ['Zoning', zoning],
        ['Buildings', buildings],
        ['Lot size', lotSize],
        ['Lot frontage', lotFrontage],
        ['Lot depth', lotDepth],
      ],
    },
    overallAttributes: {
      hasData: hasOverallAttributesData,
      rows: [
        ['Total square footage', overallBuildingArea],
        ['Number of stories', buildingStories],
        ['Number of units', buildingUnits],
        ['Year built', yearBuilt],
        ['Style', buildingStyle],
        ['Class', buildingClass],
        ['Construction type', constructionType],
      ] as ColumnValue[][],
    },
    interiorDimensions: {
      hasData: hasInteriorDimensionsData,
      rows: [
        ['Total finished areas', dimensionsBuildingArea],
        ['Additions', additions],
        ['Attics', attics],
        ['Garage', garage],
        ['Basement', basement],
      ],
    },
    roomCounts: {
      hasData: hasRoomCountsData,
      rows: [
        ['Total room count', roomsCount],
        ['Bedrooms', bedrooms],
        ['Bathrooms', bathrooms],
        ['Basement', buildingBathBasement],
      ] as ColumnValue[][],
    },
  };
}
